import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { Badge, Icon } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import { AD_TYPE_LABEL, int, isVideo } from "./formatters";
import FormatBadge from "./FormatBadge";
import PlayOverlay from "./PlayOverlay";

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 6px",
    borderRadius: 8,
    cursor: "pointer",
    "&:hover": { background: "#F6F8FA" },
  },
  thumb: {
    width: 40,
    height: 40,
    borderRadius: 6,
    objectFit: "cover",
    flexShrink: 0,
    background: tokens.inputFill,
  },
  thumbEmpty: {
    width: 40,
    height: 40,
    borderRadius: 6,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: tokens.inputFill,
    color: tokens.textHint,
  },
  body: { flex: 1, minWidth: 0 },
  top: { display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" },
  name: {
    fontFamily: tokens.fontBody,
    fontWeight: 600,
    fontSize: 13,
    color: "#232D2F",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 210,
  },
  sub: {
    fontFamily: tokens.fontBody,
    fontSize: 11.5,
    color: tokens.textSecondary,
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  live: {
    fontFamily: tokens.fontBody,
    fontSize: 11,
    color: tokens.textHint,
    marginTop: 3,
  },
  chev: { color: tokens.textHint, flexShrink: 0 },
});

const statusTone = (status) => {
  const value = String(status || "").toUpperCase();
  if (value === "ACTIVE") return "success";
  if (value === "PAUSED") return "warning";
  if (value.includes("DISAPPROVED") || value.includes("REJECTED")) return "error";
  return "neutral";
};

export default function VariantList({ variants = [], onSelect }) {
  const classes = useStyles();

  return (
    <Box>
      {variants.map((variant) => {
        const key = `${variant.templateId}:${variant.variantIndex}`;
        const live = variant.liveAd;
        const status = live && (live.adEffectiveStatus || live.adStatus);
        return (
          <Box
            key={key}
            className={classes.row}
            data-testid={`variant-${key}`}
            onClick={() => onSelect && onSelect(variant)}
          >
            {variant.imageUrl ? (
              <PlayOverlay show={isVideo(variant)}>
                <img src={variant.imageUrl} alt="" className={classes.thumb} />
              </PlayOverlay>
            ) : (
              <Box className={classes.thumbEmpty}>
                <Icon name="layout-grid" size={16} />
              </Box>
            )}

            <Box className={classes.body}>
              <Box className={classes.top}>
                <Typography
                  component="div"
                  className={classes.name}
                  title={variant.adName || ""}
                >
                  {variant.adName || `Variant ${variant.variantIndex + 1}`}
                </Typography>
                <Badge tone="neutral" subtle>
                  {AD_TYPE_LABEL[variant.adType] || variant.adType || "—"}
                </Badge>
                <FormatBadge item={variant} />
                {live ? (
                  <Badge tone={statusTone(status)} subtle>{status || "LIVE"}</Badge>
                ) : (
                  <Badge tone="neutral" subtle>Not launched</Badge>
                )}
              </Box>

              {variant.headline ? (
                <Typography component="div" className={classes.sub} title={variant.headline}>
                  {variant.headline}
                </Typography>
              ) : null}

              {live ? (
                <Typography component="div" className={classes.live}>
                  {int(live.spend)} spend · {int(live.impressions)} impressions ·{" "}
                  {int(live.messages)} messages · {int(live.leads)} leads
                </Typography>
              ) : null}
            </Box>

            <Icon name="arrow-right" size={16} style={{ color: tokens.textHint }} />
          </Box>
        );
      })}
    </Box>
  );
}
